import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useStores } from "../../stores";
import { rolesWithId } from "../../constants/constants";
import MailPreview from "./emailPreview";
import { apiPostPut } from "../../api/api_methods";
import adminAuthService from "../../services/authService";

export default function ActionButton({ status, row, action }) {
  const { clientsStore } = useStores();
  const [showPreview, setShowPreview] = useState(false);
  const [dropdownValue, setDropdownValue] = useState("");
  const [showLockedMenu, setShowLockedMenu] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  // Track dropdown position; top/left are absolute page coords.
  // We also keep an approximate menu height to decide whether to open
  // above or below the button so that the last row doesn't overflow
  // off the screen.
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
  });

  // Calculate menu position when dropdown opens or window scrolls/resizes
  useEffect(() => {
    const updateMenuPosition = () => {
      if (showLockedMenu && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        
        // Approximate menu height (2 items + padding - removed View Form)
        const APPROX_MENU_HEIGHT = 80;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const openUp = spaceBelow < APPROX_MENU_HEIGHT + 8;

        // Since dropdown uses 'fixed' positioning, use viewport coordinates directly
        // (getBoundingClientRect() already gives viewport coordinates)
        const top = openUp
          ? buttonRect.top - APPROX_MENU_HEIGHT - 4
          : buttonRect.bottom + 4;

        setMenuPosition({
          top: top, // No scroll offset needed for 'fixed' positioning
          left: buttonRect.left, // No scroll offset needed for 'fixed' positioning
        });
      }
    };

    if (showLockedMenu) {
      // Initial position calculation
      updateMenuPosition();
      
      // Recalculate after menu renders to use actual height
      const timeoutId = setTimeout(() => {
        if (menuRef.current && buttonRef.current) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const menuRect = menuRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          
          const spaceBelow = viewportHeight - buttonRect.bottom;
          const actualMenuHeight = menuRect.height;
          const openUp = spaceBelow < actualMenuHeight + 8;

          // Use viewport coordinates directly for 'fixed' positioning
          const top = openUp
            ? buttonRect.top - actualMenuHeight - 4
            : buttonRect.bottom + 4;

          setMenuPosition({
            top: top, // No scroll offset needed for 'fixed' positioning
            left: buttonRect.left, // No scroll offset needed for 'fixed' positioning
          });
        }
      }, 0);

      window.addEventListener("scroll", updateMenuPosition, true);
      window.addEventListener("resize", updateMenuPosition);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("scroll", updateMenuPosition, true);
        window.removeEventListener("resize", updateMenuPosition);
      };
    }
  }, [showLockedMenu]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setShowLockedMenu(false);
      }
    };

    if (showLockedMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLockedMenu]);

  const sendReport = async () => {
    setShowPreview(true);
  };

  // Function to send automatic notification email to financial planner
  const sendStatusUpdateNotification = async (
    newStatus,
    formData,
    commentText
  ) => {
    try {
      // Resolve the assigned agent from the store so we can safely pick email/name
      const assignedAgentId =
        typeof formData.assignedTo === "string"
          ? formData.assignedTo
          : formData.assignedTo?._id || formData.assignedTo?.id;

      const assignedAgentFromStore = Array.isArray(clientsStore.agents)
        ? clientsStore.agents.find((agent) => agent?._id === assignedAgentId)
        : null;

      const financialPlannerEmail =
        formData.assignedTo?.email ||
        assignedAgentFromStore?.email ||
        formData.agentEmail;
      const financialPlannerName =
        formData.assignedTo?.name ||
        formData.assignedTo?.displayName ||
        formData.assignedTo?.fullName ||
        assignedAgentFromStore?.name ||
        assignedAgentFromStore?.displayName ||
        assignedAgentFromStore?.fullName ||
        formData.agentName ||
        "Financial Planner";
      if (!financialPlannerEmail) {
        console.warn("No financial planner email found for notification");
        return;
      }
      const statusMessages = {
        0: "Unassigned",
        1: "All Awaken",
        2: "Total Assigned",
        3: "Submitted for Approval",
        4: "Approved by Super Financial Planner",
        5: "Shared with Client",
        6: "Locked Awaken",
        7: "Client Rejected",
        8: "Report Available",
      };
      
      // Helper to avoid showing "N/A" in emails
      const displayOrDash = (value) => {
        if (value === 0) return "0";
        return value ? value : "-";
      };

      // Get UID from various possible fields
      const uid =
        formData.factFinderId ||
        formData.factFinderUId ||
        formData.uId ||
        formData.uid ||
        formData.formId ||
        formData._id ||
        "-";
      
      // Get client details
      const clientName =
        formData.awareness?.personalDetails?.name ||
        formData.awareness?.personalDetails?.fullName ||
        formData.fullName ||
        formData.clientName ||
        formData.name ||
        "-";
      const clientEmail =
        formData.awareness?.personalDetails?.email ||
        formData.awareness?.personalDetails?.officialEmail ||
        formData.personalDetails?.email ||
        formData.email ||
        "-";
      const clientMobile =
        formData.awareness?.personalDetails?.phone ||
        formData.awareness?.personalDetails?.mobile ||
        formData.awareness?.personalDetails?.mobileNumber ||
        formData.phone ||
        formData.mobile ||
        "-";
      const clientPan =
        formData.awareness?.personalDetails?.pan ||
        formData.pan ||
        formData.panNumber ||
        "";
      const clientDob = formData.awareness?.personalDetails?.dob || "-";
      
      // Plan details
      const planTypeRaw =
        formData.awareness?.planDetails?.planType || formData.planType || "-";
      const planType = planTypeRaw !== "-" ? clientsStore.camelToTitleCase(planTypeRaw) : "-";
      const planYear =
        formData.awareness?.planDetails?.planYear || formData.planYear || "-";
      const planDuration =
        formData.awareness?.planDetails?.planDuration ||
        formData.planDuration ||
        "-";
      
      // Get admin name who changed the status
      const changedByName = clientsStore.user?.name || clientsStore.user?.email || "Admin";
      const changedByDisplayName = clientsStore.user?.displayName || changedByName;
      
      const statusMessage = statusMessages[newStatus] || "Status Updated";
      const subject = `Form Status Updated - ${statusMessage}`;
      
      const emailBody = `
        <div style="font-family: 'Segoe UI', Arial, sans-serif; background-color: #f5f7fb; padding: 24px;">
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 640px; margin: 0 auto; background: #ffffff; border-radius: 12px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06); overflow: hidden;">
            <tr>
              <td style="padding: 20px 24px 14px; text-align: center; border-bottom: 1px solid #e5e7eb;">
                <div style="display: inline-block; padding: 10px 16px; background: #111827; color: #ffffff; border-radius: 999px; font-weight: 600; letter-spacing: 0.2px;">Awaken - Financial Journey</div>
                <h2 style="margin: 14px 0 6px; color: #111827; font-size: 20px;">Form status updated</h2>
                <p style="margin: 0; color: #4b5563; font-size: 14px;">A form assigned to you has been updated.</p>
              </td>
            </tr>
            <tr>
              <td style="padding: 18px 24px 8px;">
                <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px; width: 36%;">UID</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(uid)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Name</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientName)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Email</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientEmail)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Client Mobile</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientMobile)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">DOB</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientDob)}</td>
                  </tr>
                  ${
                    clientPan
                      ? `<tr>
                          <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">PAN</td>
                          <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientPan)}</td>
                        </tr>`
                      : ""
                  }
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Type</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planType)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Year</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planYear)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Plan Duration</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planDuration)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Assigned To</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(financialPlannerName)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">New Status</td>
                    <td style="padding: 10px 0; color: #059669; font-size: 14px; font-weight: 700;">${statusMessages[newStatus] || "Updated"}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px;">Changed By</td>
                    <td style="padding: 10px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(changedByDisplayName)}</td>
                  </tr>
                  ${
                    commentText
                      ? `<tr>
                          <td style="padding: 14px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.4px; vertical-align: top;">Admin Comment</td>
                          <td style="padding: 14px 0; color: #111827; font-size: 14px; line-height: 1.6; background: #f9fafb; border-radius: 10px; padding: 14px 16px; border: 1px solid #e5e7eb;">${commentText}</td>
                        </tr>`
                      : ""
                  }
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding: 0 24px 20px; text-align: center; color: #6b7280; font-size: 13px;">
                <p style="margin: 0 0 6px;">If you have any questions, please reply to this email.</p>
                <p style="margin: 0; color: #111827; font-weight: 600;">Regards,<br>${displayOrDash(changedByDisplayName)}<br>Awaken - Financial Journey</p>
              </td>
            </tr>
          </table>
        </div>
      `;
      
      const emailData = {
        to: [financialPlannerEmail],
        subject: subject,
        html: emailBody,
        from: `${changedByDisplayName} (Awaken) <awaken@mainstream.co.in>`,
      };
      
      await apiPostPut(emailData, "/adminPanel/send-email", "POST");
    } catch (error) {
      console.error("Error sending status update notification:", error);
    }
  };

  // Function to handle status updates
  const handleStatusUpdate = async (newStatus, commentText, skipNotification = false) => {
    try {
      const response = await apiPostPut(
        commentText
          ? { status: newStatus, comment: commentText }
          : { status: newStatus },
        `/adminPanel/change-form-status/${row._id}`,
        "PUT"
      );
      if (response.status === 200) {
        console.log("Status updated successfully to:", newStatus);
        if (action) {
          await action();
        }
        // Send notification only if not skipped (skip when email is already sent from emailPreview)
        if (!skipNotification) {
          await sendStatusUpdateNotification(newStatus, row, commentText);
        }
      } else {
        console.error("Failed to update status:", response.body?.message);
        alert("Failed to update status. Please try again.");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating status. Please try again.");
    }
  };

  return (
    <>
      {status === 0 && (
        <button
          className="w-fit px-3 bg-primary opacity-90 hover:opacity-100 text-white py-[1vh] rounded-lg"
          onClick={action}
        >
          Assign
        </button>
      )}
      {(status === 1 || status === 2) &&
        (clientsStore.user.role === rolesWithId.agent ? (
          <button
            className="w-fit px-3 bg-primary opacity-90 hover:opacity-100 text-white py-[1vh] rounded-lg"
            onClick={() => {
              const formLink = adminAuthService.generateFormLink(row._id, rolesWithId.agent);
              if (formLink) {
                window.open(formLink, "_blank");
              } else {
                alert('Authentication required to open form');
              }
            }}
          >
            View Form
          </button>
        ) : (
          <button
            className="w-fit px-3 border-2 border-[#FFE500] text-gray-600 py-[1vh] rounded-lg hover:bg-yellow-50 transition-colors cursor-pointer"
            onClick={() => {
              const formLink = adminAuthService.generateFormLink(row._id, clientsStore.user.role);
              if (formLink) {
                window.open(formLink, "_blank");
              } else {
                alert('Authentication required to open form');
              }
            }}
          >
            Financial planning in progress
          </button>
        ))}
      {status === 3 &&
        (clientsStore.user.role === rolesWithId.agent ? (
          <button className="w-fit px-3 border-2 border-[#FFE500] text-gray-400 py-[1vh] rounded-lg cursor-default">
            Evaluation in progress
          </button>
        ) : (
          <button
            className=" w-fit px-3 border border-green-500 py-[1vh] rounded-lg"
            onClick={() => {
              const formLink = adminAuthService.generateFormLink(row._id, rolesWithId.admin);
              if (formLink) {
                window.open(formLink, "_blank");
              } else {
                alert('Authentication required to open form');
              }
            }}
          >
            Evaluate
          </button>
        ))}
      {status === 4 &&
        (!showPreview ? (
          <button
            className="w-fit px-3 border border-[#0051FF] py-[1vh] rounded-lg text-xs text-black"
            onClick={sendReport}
          >
            Send Report to the Client
          </button>
        ) : (
          <MailPreview
            mail={showPreview}
            setMail={setShowPreview}
            emails={[row.awareness?.personalDetails?.email || row.email || ""]}
            name="John Doe"
            sub="Sample Subject"
            mess="Sample message content..."
            currentStatus={status}
            onStatusUpdate={handleStatusUpdate}
            formId={row._id}
            // Prefer backend-provided report URL (saved via /saveReportUrl) when available
            reportUrl={row.reportUrl}
            // Tell MailPreview to use the new standardized report email endpoint
            useReportEmailEndpoint={true}
            clientName={
              row.awareness?.personalDetails?.name || row.name || "Client"
            }
            planType={
              row.awareness?.planDetails?.planType ||
              row.planType ||
              "Financial Plan"
            }
          />
        ))}
      {status === 5 &&
        (clientsStore.user.role === rolesWithId.admin ||
          clientsStore.user.role === rolesWithId.financialPlanner) && (
          <>
            <select
              value={dropdownValue}
              onChange={(e) => {
                const value = e.target.value;
                setDropdownValue(value);
                if (value === "approved") {
                  handleStatusUpdate(8); // Locked (final, view-only)
                  setDropdownValue("");
                } else if (value === "rejected") {
                  handleStatusUpdate(7); // Client Rejected
                  const formLink = adminAuthService.generateFormLink(row._id, rolesWithId.admin);
                  if (formLink) {
                    window.open(formLink, "_blank");
                  } else {
                    alert('Authentication required to open form');
                  }
                  setDropdownValue("");
                } else if (value === "viewForm") {
                  const formLink = adminAuthService.generateFormLink(row._id, rolesWithId.admin);
                  if (formLink) {
                    window.open(formLink, "_blank");
                  } else {
                    alert('Authentication required to open form');
                  }
                  setDropdownValue("");
                } else if (value === "viewReport") {
                  // Prefer saved reportUrl from backend, fallback to generated link
                  const reportLink = row.reportUrl || 
                    adminAuthService.generateReportLink(row._id, rolesWithId.admin);
                  if (reportLink) {
                    window.open(reportLink, "_blank");
                  } else {
                    alert('Report link not available. Please ensure the report has been saved.');
                  }
                  setDropdownValue("");
                }
              }}
              className="px-3 py-2 border rounded-lg text-sm mr-2"
            >
              <option value="" disabled>
                Select Action
              </option>
              <option value="approved">Client Approved</option>
              <option value="rejected">Client Rejected</option>
              <option value="viewForm">View Form</option>
              <option value="viewReport">View Report</option>
            </select>
          </>
        )}
      {status === 6 && (
        <button
          className=" w-fit px-3 border border-green-500 bg-green-500 py-[1vh] rounded-lg text-white"
          onClick={() => handleStatusUpdate(8)}
          title="Move this form to Locked Awaken"
        >
          Client Approved
        </button>
      )}
      {status === 7 &&
        (clientsStore.user.role === rolesWithId.admin ||
          clientsStore.user.role === rolesWithId.financialPlanner) && (
          <span
            className="w-fit px-3 border border-[#e05a5a] bg-[#e05a5a] py-[1vh] rounded-lg text-white cursor-pointer inline-block"
            onClick={() => {
              const formLink = adminAuthService.generateFormLink(row._id, rolesWithId.admin);
              if (formLink) {
                window.open(formLink, "_blank");
              } else {
                alert('Authentication required to open form');
              }
            }}
            title="Click to view/edit form"
          >
            Client Rejected (Click to View/Edit)
          </span>
        )}
      {status === 8 && (
        <>
          <div className="relative inline-block">
            <button
              ref={buttonRef}
              className="w-fit px-4 py-2 border border-green-500 bg-green-500 rounded-lg text-white hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-1"
              onClick={() => setShowLockedMenu(!showLockedMenu)}
            >
              View Options
              <span className={`inline-block transition-transform ${showLockedMenu ? 'rotate-180' : ''}`}>▼</span>
            </button>
          </div>
          {showLockedMenu &&
            createPortal(
              <div
                ref={menuRef}
                className="fixed bg-white border border-gray-300 rounded-lg shadow-lg z-[9999] min-w-[180px] py-1"
                style={{
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`,
                }}
              >
                <button
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors flex items-center gap-2"
                  onClick={() => {
                    // Use saved report URL from backend when available; fall back to tokenized link
                    const reportLink =
                      row.reportUrl ||
                      adminAuthService.generateReportLink(
                        row._id,
                        rolesWithId.admin
                      );
                    if (reportLink) {
                      window.open(reportLink, "_blank");
                    } else {
                      alert("Authentication required to view report");
                    }
                    setShowLockedMenu(false);
                  }}
                >
                  <span>📊</span>
                  <span>View Report</span>
                </button>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors flex items-center gap-2"
                  onClick={() => {
                    // Pre-select agent based on user role
                    const currentUser = clientsStore?.user;
                    const isFinancialPlanner = currentUser?.role === rolesWithId.agent;
                    if (isFinancialPlanner && currentUser?._id) {
                      setSelectedAgentId(currentUser._id);
                    } else {
                      setSelectedAgentId("");
                    }
                    setShowLockedMenu(false);
                    setShowRenewalModal(true);
                  }}
                >
                  <span>🔄</span>
                  <span>Create Renewal</span>
                </button>
              </div>,
              document.body
            )}
        </>
      )}

      {/* Create Renewal Modal */}
      {showRenewalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Create Renewal Form
            </h3>
            
            {/* Client Information */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Client Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <p className="font-medium">
                    {row.awareness?.personalDetails?.name || 
                     row.clientName || 
                     row.name || 
                     "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Plan Type:</span>
                  <p className="font-medium">
                    {row.awareness?.planDetails?.planType || 
                     row.planType || 
                     "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <p className="font-medium">
                    {row.awareness?.personalDetails?.email || 
                     row.email || 
                     "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Form ID:</span>
                  <p className="font-mono font-semibold">{row._id || "N/A"}</p>
                </div>
              </div>
            </div>

            {/* Financial Planner Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Financial Planner
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                disabled={clientsStore.user?.role === rolesWithId.agent}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Financial Planner</option>
                {Array.isArray(clientsStore.agents) &&
                  clientsStore.agents.map((agent) => (
                    <option key={agent._id} value={agent._id}>
                      {agent.name || agent.email}
                    </option>
                  ))}
              </select>
              {clientsStore.user?.role === rolesWithId.agent && (
                <p className="mt-1 text-xs text-gray-500">
                  You are automatically assigned as the financial planner
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowRenewalModal(false);
                  setSelectedAgentId("");
                }}
                className="px-4 py-2 rounded-lg text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedAgentId) {
                    alert("Please select a financial planner");
                    return;
                  }

                  try {
                    // Mark the original form as having a renewal created
                    // Backend will automatically filter this from locked awakens
                    try {
                      const markRenewalResponse = await apiPostPut(
                        {
                          renewalCreated: true,
                          renewalCreatedDate: new Date().toISOString(),
                          renewalCreatedBy: clientsStore.user._id,
                        },
                        `/adminPanel/change-form-status/${row._id}`,
                        "PUT"
                      );
                      
                      if (markRenewalResponse.status === 200) {
                        console.log("✅ Original form marked as having renewal created");
                      } else {
                        console.warn("⚠️ Failed to mark form as renewed, but continuing with renewal creation");
                      }
                    } catch (markError) {
                      console.error("Error marking form as renewed:", markError);
                      // Continue with renewal creation even if marking fails
                    }

                    // Create renewal form with same data but updated plan year
                    const currentYear = new Date().getFullYear();
                    const renewalLink = adminAuthService.generateRenewalFormLink(
                      row._id,
                      currentYear,
                      clientsStore.user.role,
                      selectedAgentId
                    );
                    if (renewalLink) {
                      window.open(renewalLink, "_blank");
                      setShowRenewalModal(false);
                      setSelectedAgentId("");
                      
                      // Refresh the data to update the UI (remove from locked awakens)
                      if (action) {
                        setTimeout(() => {
                          action();
                        }, 1000);
                      }
                    } else {
                      alert("Authentication required to create renewal form");
                    }
                  } catch (error) {
                    console.error("Error creating renewal:", error);
                    alert("Error creating renewal. Please try again.");
                  }
                }}
                className="px-4 py-2 rounded-lg text-white bg-purple-600 hover:bg-purple-700 transition-colors"
              >
                Create Renewal
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
